/**
 * useFolderSearch · 文件夹关键词搜索的状态管理（防抖 + 竞态防护）
 *
 * 由 FolderSearchInput 调用：输入 → 500ms 防抖 → 调接口 2（/web/folder/search）。
 * searching 覆盖"防抖等待 + 请求中"整个窗口；seq 序号丢弃过期响应，
 * 保证展示的结果永远对应最新一次输入。搜索失败静默降级为空结果
 * （搜索是辅助路径，不打断用户）。
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { searchFolders, FolderSearchItem } from '../../services/folderSearch'

/** 输入停止后多久发起请求 */
const SEARCH_DEBOUNCE_MS = 500
/** 下拉列表最多展示条数（接口 limit 同值） */
const SEARCH_RESULT_LIMIT = 30

export interface UseFolderSearchResult {
  /** 当前输入框关键词（未 trim 的原始值，供受控 Input 回显） */
  keyword: string
  /** 输入变化入口 · 内部处理防抖与空关键词清场 */
  onKeywordChange: (value: string) => void
  /** 最新一次搜索的命中列表 */
  results: FolderSearchItem[]
  /** 防抖等待或请求进行中 */
  searching: boolean
  /** 已有一次完成的搜索（区分"还没搜"与"搜到空"，空态文案据此显示） */
  searched: boolean
}

/**
 * 管理搜索框的关键词、防抖请求与结果状态。
 *
 * @param groupID - 当前团队 UUID，未就绪（空）时不发请求
 * @returns 关键词与搜索状态，见 UseFolderSearchResult
 */
export function useFolderSearch(groupID: string): UseFolderSearchResult {
  const [keyword, setKeyword] = useState('')
  const [results, setResults] = useState<FolderSearchItem[]>([])
  const [searching, setSearching] = useState(false)
  const [searched, setSearched] = useState(false)

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** 请求序号 · 响应回来时与最新序号不一致即丢弃（竞态防护） */
  const seqRef = useRef(0)

  // 卸载时停掉挂起的防抖任务，并使在途响应失效
  useEffect(
    () => () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      seqRef.current += 1
    },
    []
  )

  const onKeywordChange = useCallback(
    (value: string) => {
      setKeyword(value)
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)

      const trimmed = value.trim()
      if (!trimmed || !groupID) {
        // 清空关键词：作废在途请求并重置全部状态
        seqRef.current += 1
        setResults([])
        setSearching(false)
        setSearched(false)
        return
      }

      setSearching(true)
      debounceTimerRef.current = setTimeout(() => {
        const seq = ++seqRef.current
        searchFolders({
          groupID,
          keyword: trimmed,
          limit: SEARCH_RESULT_LIMIT
        })
          .then((list) => {
            if (seq !== seqRef.current) return
            setResults(list)
            setSearching(false)
            setSearched(true)
          })
          .catch((err) => {
            if (seq !== seqRef.current) return
            // 静默降级为空结果 · 搜索失败不打断用户主流程
            // eslint-disable-next-line no-console
            console.warn('[FolderSearch] 搜索失败', err)
            setResults([])
            setSearching(false)
            setSearched(true)
          })
      }, SEARCH_DEBOUNCE_MS)
    },
    [groupID]
  )

  return { keyword, onKeywordChange, results, searching, searched }
}
